import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { message, Modal } from "antd";

import useOptions from "features/masterData/hooks/useOptions";
import { generateRandomString } from "utils/helper";
import * as services from "../services/companyApi";
import { setCurrentOvertime, setIsLoading } from "../slices/companySlice";
import { useState } from "react";
import dayjs from "dayjs";

function toInitialValues(initialValues = {}) {
  const { breakPeriodType = {}, otReqCondition = {} } = initialValues;

  const modifiedValues = {
    ...initialValues,
    minimumRequired: initialValues?.minimumRequired ? convertTimeToMinutes(initialValues?.minimumRequired) : "00",

    workingConditionId: initialValues.id,
    advanceLessThan: initialValues?.advanceLessThan ? initialValues?.advanceLessThan : "1",
    advanceAtLeast: initialValues?.advanceAtLeast ? initialValues?.advanceAtLeast : "1",
    allowOtUnCompleteDays: initialValues.allowOtUnCompleteDays === "Y",
    breakPeriodType:
      breakPeriodType?.id && breakPeriodType?.id !== "missing"
        ? breakPeriodType?.id
        : null,
    breakPeriod: initialValues?.breakPeriod ? convertTimeToMinutes(initialValues?.breakPeriod) : "00",
    workingPeriodBeforeStartBreakDeduction: initialValues?.workingPeriodBeforeStartBreakDeduction ? initialValues?.workingPeriodBeforeStartBreakDeduction : "00:00:00",
    breakDeductionEveryWorkingPeriod: initialValues?.breakDeductionEveryWorkingPeriod ? initialValues?.breakDeductionEveryWorkingPeriod : "00:00:00",
    calculateOtHolidayByShift: initialValues.calculateOtHolidayByShift === "Y",
    deductBreakHolidayShift: initialValues.deductBreakHolidayShift === "Y",
    docLimitPerDay:
      initialValues.docLimitPerDay === " " ? "1" : initialValues.docLimitPerDay,
    calculateOtEvery: initialValues?.calculateOtEvery ? initialValues?.calculateOtEvery : "0",
    otReqCondition:
      otReqCondition.id && otReqCondition.id !== "missing"
        ? otReqCondition.id
        : null,
    otRounding: initialValues.otRounding === "Y",
    payOtFlexShifts: initialValues.payOtFlexShifts === "Y",
    permanentEmployeesOnly: initialValues.permanentEmployeesOnly === "Y",
    limitovertimePerWeek: initialValues.limitovertimePerWeek === "Y",
    supervisorRequest: initialValues.supervisorRequest === "Y",
    employeeRequest: initialValues.employeeRequest === "Y",
  };




  return modifiedValues;
}

function convertTimeToMinutes(time) {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return (hours * 60) + minutes + Math.floor(seconds / 60);
}

const time = "01:30:00";
console.log(convertTimeToMinutes(time)); // Output: 90

function convertMinutesToTimeWithSeconds(minutes) {
  const hours = Math.floor(minutes / 60).toString().padStart(2, '0');
  const mins = (minutes % 60).toString().padStart(2, '0');
  const seconds = '00';
  return `${hours}:${mins}:${seconds}`;
}


// const calculateOtEvery = 60;
function toParams(values) {
  const modifiedValues = {
    ...values,
    allowOtUnCompleteDays: values.allowOtUnCompleteDays ? "Y" : "N",
    calculateOtHolidayByShift: values.calculateOtHolidayByShift ? "Y" : "N",
    deductBreakHolidayShift: values.deductBreakHolidayShift ? "Y" : "N",
    otRounding: values.otRounding ? "Y" : "N",
    payOtFlexShifts: values.payOtFlexShifts ? "Y" : "N",
    permanentEmployeesOnly: values.permanentEmployeesOnly ? "Y" : "N",
    limitovertimePerWeek: values?.limitovertimePerWeek ? "Y" : "N",
    supervisorRequest: values?.supervisorRequest ? "Y" : "N",
    employeeRequest: values?.employeeRequest ? "Y" : "N",
  };
  return modifiedValues;
}

function useCompanyOvertime(props) {
  const { onCancel = () => { } } = props;
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.company.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const currentOvertime = useSelector((state) => state.company.currentOvertime);

  const {
    otReqConditionOptions = [],
    breakPeriodTypeOptions = [],
    getOtReqConditionOptions,
    getBreakPeriodTypeOptions,
  } = useOptions();

  const initialValues = useMemo(() => {
    return toInitialValues(currentOvertime);
  }, [currentOvertime]);

  const getCompanyOvertime = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getOvertimeCondition({
        requestId: generateRandomString(),
        companyId: currentCompany,
      });
      dispatch(setCurrentOvertime(response.data));
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch]);

  const addOvertimeCondition = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.addOvertimeCondition(values);
        message.success(t("common.success"))
        onCancel();
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, onCancel]
  );
  const { t, i18n } = useTranslation();

  const handleCancel = useCallback(() => {
    Modal.confirm({
      title: t("dialog.confirmation.header"),
      content:
        i18n.language === "en"
          ? "Are you sure you want to cancel the changes?"
          : "คุณต้องการยกเลิกการแก้ไขใช่หรือไม่ ?",
      onOk() {
        onCancel();
      },
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
    });
  }, [onCancel, t, i18n.language]);

  const editOvertimeCondition = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        values.calculateOtEvery = values?.otRounding === "Y" ? values.calculateOtEvery : null;
        await services.editOvertimeCondition(values);
        message.success(t("common.success"))
        onCancel();
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, onCancel]
  );

  function minuteCal(params) {
    if (!params || params < 0) return "00:00:00";

    if (params < 60) {
      let padded = String(params).padStart(2, '0');
      return `00:${padded}:00`;
    }

    let hours = Math.floor(params / 60);
    let minutes = params % 60;
    let paddedHours = String(hours).padStart(2, '0');
    let paddedMinutes = String(minutes).padStart(2, '0');

    return `${paddedHours}:${paddedMinutes}:00`;
  }


  const onSubmit = useCallback(
    async (values) => {
      const modifiedValues = toParams({
        ...values,
        minimumRequired: minuteCal(values?.minimumRequired),
        breakPeriod: minuteCal(values?.breakPeriod),
        requestId: generateRandomString(),
        companyId: currentCompany,
        actionId: currentEmployee,
        workingConditionId: initialValues?.id,
      });
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? "Are you sure confirm to edit company working condition ?"
          : "คุณยืนยันการแก้ไขเงื่อนไขการทำงานทั่วไปใช่หรือไม่ ?"
        ),
        onOk() {
          if (currentOvertime.id && currentOvertime.id !== " ") {
            editOvertimeCondition(modifiedValues);
          } else {
            addOvertimeCondition(modifiedValues);
          }
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [currentCompany, currentEmployee, initialValues?.id, currentOvertime.id, editOvertimeCondition, addOvertimeCondition]
  );

  useEffect(() => {
    getCompanyOvertime();
  }, [getCompanyOvertime]);

  useEffect(() => {
    getOtReqConditionOptions();
  }, [getOtReqConditionOptions]);

  useEffect(() => {
    getBreakPeriodTypeOptions();
  }, [getBreakPeriodTypeOptions]);

  const [selectedBreakPeriodType, setSelectedBreakPeriodType] = useState("");
  const [selectedOtReqCondition, setSelectedOtReqCondition] = useState("");
  const [defaultValues, setDefaultValues] = useState(initialValues);

  useEffect(() => {
    const selectedBreakPeriod = breakPeriodTypeOptions.find(option => option.id === initialValues?.breakPeriodType);
    if (selectedBreakPeriod) {
      setSelectedBreakPeriodType(selectedBreakPeriod.nameEn);
    }

    const selectedOtReqCondition = otReqConditionOptions.find(option => option.id === initialValues?.otReqCondition);
    if (selectedOtReqCondition) {
      setSelectedOtReqCondition(selectedOtReqCondition.nameEn);
    }
  }, [initialValues, breakPeriodTypeOptions, otReqConditionOptions]);

  const handleBreakPeriodTypeChange = (value) => {
    const selectedOption = breakPeriodTypeOptions.find(option => option.id === value);
    if (selectedOption) {
      initialValues.breakPeriodType = value;
      setSelectedBreakPeriodType(selectedOption.nameEn);
      if (selectedOption.code === "BREAK_PERIOD_TYPE_01") {
        initialValues?.workingPeriodBeforeStartBreakDeduction ?
          initialValues.workingPeriodBeforeStartBreakDeduction = defaultValues.workingPeriodBeforeStartBreakDeduction : initialValues.workingPeriodBeforeStartBreakDeduction = dayjs("02:00:00", "HH:mm:ss");
        initialValues.breakDeductionEveryWorkingPeriod = null;
        initialValues?.breakPeriod ?
          initialValues.breakPeriod = defaultValues.breakPeriod : initialValues.breakPeriod = dayjs("00:02:00", "HH:mm:ss");
      } else if (selectedOption.code === "BREAK_PERIOD_TYPE_02") {
        initialValues.workingPeriodBeforeStartBreakDeduction = null;
        initialValues?.breakDeductionEveryWorkingPeriod ?
          initialValues.breakDeductionEveryWorkingPeriod = defaultValues.breakDeductionEveryWorkingPeriod : initialValues.breakDeductionEveryWorkingPeriod = dayjs("04:00:00", "HH:mm:ss");
        initialValues?.breakPeriod ?
          initialValues.breakPeriod = defaultValues.breakPeriod : initialValues.breakPeriod = dayjs("00:03:00", "HH:mm:ss");
      }
    }
  };

  const handleOtReqConditionChange = (value) => {
    const selectedOption = otReqConditionOptions.find(option => option.id === value);
    if (selectedOption) {
      initialValues.otReqCondition = value;
      setSelectedOtReqCondition(selectedOption.nameEn);
      if (selectedOption.code === "OVERTIME_REQUEST_CONDITION_01") {
        initialValues.advanceAtLeast = defaultValues.advanceAtLeast;
        initialValues.advanceLessThan = null;
      } else if (selectedOption.code === "OVERTIME_REQUEST_CONDITION_03") {
        initialValues.advanceAtLeast = null;
        initialValues.advanceLessThan = defaultValues.advanceLessThan;
      }
    }
  };

  return {
    isLoading,
    initialValues,
    currentOvertime,
    otReqConditionOptions,
    breakPeriodTypeOptions,
    selectedBreakPeriodType,
    selectedOtReqCondition,
    handleBreakPeriodTypeChange,
    handleOtReqConditionChange,
    onEdit: props.onEdit,
    onCancel: handleCancel,
    onSubmit,
    convertMinutesToTimeWithSeconds

  };
}

export default useCompanyOvertime;
